/**
 * ContextGridSwitcher — Top-bar dropdown that displays all CONTEXTS
 * (personas / roles) as a grid of icon tiles.
 *
 * Switching context updates the active persona, which in turn:
 *   - re-filters the sidebar and mega-menu
 *   - navigates to the context's defaultRoute
 *   - swaps quick-create actions and ProjectStackBar steps
 *
 * Immobilia-style: dark trigger pill, colored icon, grid of 9 tiles in popover.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { useContextStore } from '@/stores/context-store'
import {
  GRC_CONTEXTS,
  CONTEXT_ORDER,
  getContextMeta,
  type GrcContextId,
} from '@/config/contexts'

interface ContextGridSwitcherProps {
  align?: 'start' | 'center' | 'end'
  className?: string
}

export function ContextGridSwitcher({
  align = 'start',
  className,
}: ContextGridSwitcherProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const { activeContext, setActiveContext } = useContextStore()
  const active = GRC_CONTEXTS[activeContext] ?? GRC_CONTEXTS['associe']
  const activeMeta = getContextMeta(activeContext)

  const handleSelect = (id: GrcContextId) => {
    setActiveContext(id)
    setOpen(false)
    const ctx = GRC_CONTEXTS[id]
    if (ctx?.defaultRoute) navigate(ctx.defaultRoute)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          className={cn(
            'flex items-center gap-2 rounded-full pl-1 pr-3 py-1',
            'border bg-card hover:bg-accent transition-colors',
            'text-[12px] font-semibold whitespace-nowrap shrink-0',
            className,
          )}
        >
          <span
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded-full shrink-0 shadow-sm',
              active.accentBg,
              'text-white',
            )}
          >
            <active.icon className="h-3.5 w-3.5" />
          </span>
          <span className="flex flex-col items-start leading-tight hidden sm:flex">
            <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t('shell.context')}
            </span>
            <span className="text-[12px] font-bold text-foreground">
              {activeMeta.name}
            </span>
          </span>
          <ChevronDown
            className={cn(
              'h-3 w-3 text-muted-foreground transition-transform',
              open && 'rotate-180',
            )}
          />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align={align}
        sideOffset={8}
        className="w-[min(96vw,520px)] p-3 border shadow-2xl"
      >
        <div className="mb-2 px-1 flex items-center justify-between">
          <h4 className="text-[11px] font-bold uppercase tracking-wider text-foreground">
            {t('shell.chooseContext')}
          </h4>
          <span className="text-[10px] text-muted-foreground">
            {CONTEXT_ORDER.length} {t('shell.roles')}
          </span>
        </div>
        <ul className="grid grid-cols-3 gap-1.5">
          {CONTEXT_ORDER.map((id) => {
            const ctx = GRC_CONTEXTS[id]
            const ctxMeta = getContextMeta(id)
            const isActive = id === activeContext
            return (
              <li key={id}>
                <button
                  type="button"
                  onClick={() => handleSelect(id)}
                  title={ctxMeta.description}
                  className={cn(
                    'group relative flex h-full w-full flex-col items-start gap-2 rounded-lg p-2.5',
                    'border transition-all duration-150 text-left',
                    isActive
                      ? 'border-primary/40 bg-primary/5 shadow-sm'
                      : 'border-transparent hover:border-border hover:bg-accent/40',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-9 w-9 items-center justify-center rounded-xl shrink-0 shadow-sm',
                      ctx.accentBg,
                      'text-white',
                    )}
                  >
                    <ctx.icon className="h-4 w-4" />
                  </span>
                  <span className="flex flex-col gap-0.5 min-w-0 w-full">
                    <span
                      className={cn(
                        'text-[12px] font-semibold leading-tight truncate',
                        isActive ? 'text-primary' : 'text-foreground',
                      )}
                    >
                      {ctxMeta.name}
                    </span>
                    <span className="text-[10px] text-muted-foreground line-clamp-2 leading-tight">
                      {ctxMeta.description}
                    </span>
                  </span>
                  {isActive && (
                    <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-primary" />
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      </PopoverContent>
    </Popover>
  )
}
